// src/components/RecepcionDigital/EncomiendaCard.jsx
//
// ── Decisiones de diseño ──────────────────────────────────────────────────────
// Tarjeta de triaje del Manifiesto — NUNCA una fila de tabla (Principio 7:
// tarjetas para triaje). Cada tarjeta es una conversación activa con un cliente:
// el chip de estado dice de quién es la pelota, la vista previa del último
// mensaje da contexto sin abrir nada, y el tiempo transcurrido en español
// natural ("hace 3 h") da urgencia sin que el operador calcule fechas.
// - Un solo botón primario (rojo de marca, min 44px — Principio 10) cuyo verbo
//   cambia según el estado: "Reanudar" cuando la pelota estuvo en la cancha del
//   cliente (esperando_cliente / expirada), "Abrir en Producción" en el resto.
// - El chip de impuesto 30% aparece SOLO cuando algún paquete cruza el umbral
//   DAI — calculado con calcPackageTax (verdad Tier 1, customsCalc.js), nunca
//   con una constante local (Principio 4: señales visuales, no porcentajes de
//   confianza inventados).
// - Click en cualquier parte de la tarjeta abre el drawer de detalle; el botón
//   primario detiene la propagación y va directo a Producción.
// - Colores 100% variables CSS del tema → light/dark sin lógica extra.
// - Peso no existe en esta superficie (Principio 12).
// ──────────────────────────────────────────────────────────────────────────────

import { MessageCircle, Mic, Image as ImageIcon, Package } from 'lucide-react';
import StatusChip, { TAX_TIER_META } from './shared/StatusChip';
import { packageCrossesDai } from '../../utils/customsCalc';

// "hace 5 min" / "hace 3 h" / "hace 2 días" — español natural, sin librerías.
export const timeAgoEs = (iso) => {
  if (!iso) return '';
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'hace un momento';
  if (mins < 60) return `hace ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'hace 1 día' : `hace ${days} días`;
};

// Última actividad del CLIENTE en el hilo (los mensajes del agente no cuentan
// como "actividad del cliente" para el triaje).
const lastClientMessage = (thread) => {
  for (let i = (thread || []).length - 1; i >= 0; i--) {
    if (thread[i].from === 'client') return thread[i];
  }
  return null;
};

const MessagePreview = ({ msg }) => {
  if (!msg) {
    return (
      <span className="italic" style={{ color: 'var(--text-tertiary)' }}>
        Sin mensajes del cliente todavía
      </span>
    );
  }
  if (msg.media_type === 'audio') {
    return (
      <span className="inline-flex items-center gap-1.5">
        <Mic size={12} aria-hidden="true" />
        Nota de voz ({msg.duration_seconds || 0}s)
      </span>
    );
  }
  if (msg.media_type === 'image') {
    return (
      <span className="inline-flex items-center gap-1.5">
        <ImageIcon size={12} aria-hidden="true" />
        Foto{msg.text ? ` — “${msg.text}”` : ''}
      </span>
    );
  }
  return <>“{msg.text}”</>;
};

const EncomiendaCard = ({ draft, onOpen, onOpenDetail }) => {
  const preview = lastClientMessage(draft.whatsapp_thread);
  const pkgCount = (draft.packages || []).length;
  const itemCount = (draft.packages || []).reduce(
    (sum, p) => sum + (p.items || []).length, 0
  );

  // Umbral DAI por paquete — verdad Tier 1 (customsCalc). Chip solo si aplica.
  const crossesThreshold = (draft.packages || []).some(
    (p) => packageCrossesDai(p.items, draft.customs_config_snapshot) === true
  );
  const tier30 = TAX_TIER_META['30%'];

  const resumable = draft.status === 'esperando_cliente' || draft.status === 'expirada';
  const primaryLabel = resumable ? 'Reanudar' : 'Abrir en Producción';

  const handleCardKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpenDetail(draft.id);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpenDetail(draft.id)}
      onKeyDown={handleCardKey}
      aria-label={`Ver detalle de la encomienda de ${draft.client?.name || 'cliente'}`}
      className="text-left w-full rounded-lg border p-4 cursor-pointer transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
      style={{
        background: 'var(--tile)',
        borderColor: 'var(--border)',
        boxShadow: 'var(--tile-shadow)',
        outlineColor: 'var(--avianca-red)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--border-hover)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
    >
      {/* Cliente + chips */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-main)' }}>
            {draft.client?.name}
          </p>
          <p
            className="mt-0.5 flex items-center gap-1.5 text-xs"
            style={{ color: 'var(--text-secondary)' }}
          >
            <MessageCircle size={12} style={{ color: 'var(--green)' }} aria-hidden="true" />
            {draft.client?.phone}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <StatusChip status={draft.status} />
          {crossesThreshold && (
            <span
              className="inline-flex items-center rounded-md border text-[11px] px-2 py-0.5 font-medium"
              style={{ background: tier30.bg, color: tier30.color, borderColor: tier30.border }}
              aria-label="Cruza el umbral de impuesto DAI"
            >
              {tier30.label}
            </span>
          )}
        </div>
      </div>

      {/* Último mensaje del cliente */}
      <p
        className="mt-3 text-xs leading-relaxed truncate"
        style={{ color: 'var(--text-secondary)' }}
        title={preview?.text || undefined}
      >
        <MessagePreview msg={preview} />
      </p>

      {/* Conteos + tiempo + acción primaria */}
      <div className="mt-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-tertiary)' }}>
          <Package size={12} aria-hidden="true" />
          <span>
            {pkgCount} {pkgCount === 1 ? 'paquete' : 'paquetes'} · {itemCount}{' '}
            {itemCount === 1 ? 'artículo' : 'artículos'}
          </span>
          <span aria-hidden="true">·</span>
          <span>{timeAgoEs(draft.last_client_activity_at)}</span>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onOpen(draft.id); }}
          aria-label={`${primaryLabel} — encomienda de ${draft.client?.name || 'cliente'}`}
          className="min-h-[44px] shrink-0 px-4 rounded-lg text-xs font-semibold text-white transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50"
          style={{ background: 'var(--avianca-red)', outlineColor: 'var(--avianca-red)' }}
        >
          {primaryLabel}
        </button>
      </div>
    </div>
  );
};

export default EncomiendaCard;
